import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

const TOAST_THROTTLE_MS = 4000;

export function ReadOnlyToastHandler() {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  const lastShownRef = useRef(0);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const originalFetch = window.fetch.bind(window);

    async function openBillingPortal() {
      try {
        const token = await getTokenRef.current();
        const res = await originalFetch("/api/billing/portal", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
        if (!res.ok || !data.url) {
          toast({
            title: "Could not open billing",
            description: data.error ?? "Please try again.",
            variant: "destructive",
          });
          return;
        }
        window.location.href = data.url;
      } catch {
        toast({
          title: "Network error",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    }

    function showReadOnlyToast(message: string) {
      const now = Date.now();
      if (now - lastShownRef.current < TOAST_THROTTLE_MS) return;
      lastShownRef.current = now;
      toast({
        title: "Workspace is read-only",
        description: message,
        variant: "destructive",
        action: (
          <ToastAction
            altText="Manage billing"
            onClick={(e) => {
              e.preventDefault();
              openBillingPortal();
            }}
            data-testid="read-only-toast-cta"
          >
            Manage billing
          </ToastAction>
        ),
      });
    }

    function showDemoToast(message: string) {
      const now = Date.now();
      if (now - lastShownRef.current < TOAST_THROTTLE_MS) return;
      lastShownRef.current = now;
      toast({
        title: "Not available in demo",
        description: message,
        variant: "destructive",
      });
    }

    const patchedFetch: typeof window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      if (response.status !== 403) return response;
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) return response;
      try {
        const cloned = response.clone();
        const data = (await cloned.json()) as { code?: string; message?: string; error?: string };
        if (data?.code === "demo_mode") {
          showDemoToast(
            data.error ?? "This action is not available in the demo. Sign up for full access.",
          );
        } else if (data?.code === "read_only_mode") {
          showReadOnlyToast(
            data.message ?? "Your workspace is in read-only mode. Reactivate to continue editing.",
          );
        }
      } catch {
        // ignore parse errors; pass response through unchanged
      }
      return response;
    };

    window.fetch = patchedFetch;
    return () => {
      if (window.fetch === patchedFetch) {
        window.fetch = originalFetch;
      }
    };
  }, []);

  return null;
}
